import { AxiosError } from "axios";

const DUPLICATE_APPLICANT_ON_UNIT_CODE = "duplicate_applicant_on_unit";

type DuplicateApplicantOnUnitError = {
  code: typeof DUPLICATE_APPLICANT_ON_UNIT_CODE;
  message: string;
  conflicting_emails: string[];
};

export const getDuplicateApplicantOnUnitError = (
  error: unknown
): DuplicateApplicantOnUnitError | null => {
  if (!(error instanceof AxiosError)) return null;
  const body = error.response?.data?.error;
  if (!body || body.code !== DUPLICATE_APPLICANT_ON_UNIT_CODE) return null;

  return {
    code: DUPLICATE_APPLICANT_ON_UNIT_CODE,
    message: typeof body.message === "string" ? body.message : "",
    conflicting_emails: body.conflicting_emails
  };
};

const stringifyDetails = (details: unknown): string => {
  if (typeof details === "string") return details;
  if (Array.isArray(details))
    return details.map(stringifyDetails).filter(Boolean).join("; ");
  if (details && typeof details === "object") {
    const message = (details as { message?: string }).message;
    if (typeof message === "string") return message;
  }
  return JSON.stringify(details);
};

export const getApiErrorKey = (error: unknown): string | null => {
  if (!(error instanceof AxiosError)) return null;
  const key = error.response?.data?.error?.message;
  return typeof key === "string" ? key : null;
};

export const getErrorMessage = (error: unknown, fallback?: string) => {
  if (error instanceof AxiosError) {
    if (error.response?.data?.error?.message) {
      let ret = error.response.data?.error?.message;
      const details = error.response.data?.error?.details;
      if (details) ret += `: ${stringifyDetails(details)}`;
      return ret;
    }
    const errors = error.response?.data?.error?.errors;
    if (Array.isArray(errors))
      return errors.reduce<string>(
        (acc, err, idx) => acc + `${idx !== 0 ? ", " : ""}${String(err)}`,
        ""
      );
    // Handle errors as object with array values (e.g., {enrollment: ["message"]})
    if (errors && typeof errors === "object")
      return Object.values(errors).flat().join(", ");
    // Handle direct message format (common for 422 and other validation errors)
    if (error.response?.data?.message) return error.response.data.message;
  }

  if (error instanceof Error) return error.message;

  return fallback || "Something went wrong, please try again later";
};
